import { Show } from "shared/components";
import {
  type ColDef,
  type SortChangedEvent,
  type ICellRendererParams,
  type GridReadyEvent,
  type GridApi,
  type GetRowIdParams,
  type IsFullWidthRowParams,
  type CellClickedEvent
} from "ag-grid-community";
import { AssigneeSelect } from "../AssigneeSelect";

import { useLocation, useNavigate } from "react-router-dom";
import { ApplicantStatus, ApplicationStatus } from "shared/types";
import {
  Dispatch,
  lazy,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import { Application } from "types/application";

import { useAuthContext } from "providers/Auth";
import { RuleBaseGraph } from "shared/app/RuleBaseGraph";
import { ScoreGraphSize } from "views/applications-id/components/ApplicationScoreCard/constants";
import {
  ApplicationLike,
  getApplicationUIStatus
} from "utils/get-application-ui-status";
import { Loader } from "shared/components/Loader";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ScreeningStatusBadge } from "../ScreeningStatusBadge";
import { ApplicantProgressHover } from "../ApplicantProgressHover";
import {
  ApplicantProgressContext,
  ApplicantProgressContextValue
} from "../ApplicantProgressHover/context";
import { ActiveTab } from "./constants";
import {
  arrangeColumnDefs,
  useColumnReorder
} from "shared/components/AgGrid/useColumnReorder";
import {
  getApplicantNames,
  getCombinedNamesAndEmails,
  getInviteesEmails
} from "./helpers";
import { useLayout } from "providers/Layout/hooks";
import {
  BubbleChatIcon,
  CashbackIcon,
  File02Icon,
  PathfinderDivideIcon,
  PauseIcon,
  PlayIcon,
  Search01Icon,
  UnavailableIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import {
  placeOnHoldApplication,
  resumeApplication
} from "shared/app/OnHoldResume/api";
import { useQueryClient } from "@tanstack/react-query";
import { ApplicationActions } from "shared/utils/application-actions";
import { DateTableCell } from "shared/components/DateTableCell";
import { DateTimeTableCell } from "shared/components/DateTimeTableCell";
import { camelToSentence, capitalize } from "shared/utils";
import { toStateCode } from "utils/stateCodes";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { ChatBadge } from "shared/app/MessagePopup/components/ChatBadge";
import { useUnreadByContact } from "views/inbox/api/use-unread-by-contact";
import { Archive, ArrowRight } from "untitledui-js-base";
import {
  Checkbox,
  CheckboxSize,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import {
  AgGrid,
  DEFAULT_AG_GRID_VIEWPORT_OFFSET
} from "shared/components/AgGrid";
import { LastNoteCell } from "shared/app/Notes/components/LastNoteCell";
import { Flag02Icon } from "@hugeicons-pro/core-solid-rounded";
import { getFullName } from "shared/utils/get-full-name";
import { formatCalendar } from "views/boom-notifications/helpers/format-calendar";
import { track } from "shared/telemetry/track";
import { ApplicationStageSelect } from "shared/app/ApplicationStage/ApplicationStageSelect";
import { StagePill } from "shared/app/ApplicationStage/StagePill";
import { ColumnFilterHeader } from "../ColumnFilters/ColumnFilterHeader";
import { useLivePresence } from "shared/app/LiveView/useLivePresence";
import { LivePresenceContext } from "shared/app/LiveView/LivePresenceContext";
import {
  LIVE_VIEW_ROW_ACTION_ATTR,
  LivePresenceDot,
  LiveViewRowAction,
  VIEW_LIVE_PARAM
} from "shared/app/LiveView/LivePresenceDot";

const ScoreGraph = lazy(
  () =>
    import("views/applications-id/components/ApplicationScoreCard/ScoreGraph")
);

type ApplicationWithUIStatus = Application & {
  ui_status?: ApplicationStatus;
  isGroupHeader?: boolean;
  groupValue?: string;
};

const getNestedValue = (obj: unknown, path: string): unknown => {
  return path
    .split(".")
    .reduce<unknown>(
      (acc, part) => (acc as Record<string, unknown> | null)?.[part],
      obj
    );
};

const preserveRowOrderComparator = () => 0;

// Columns whose header offers a filter (see utils/columnFilters); the header hides the
// button itself when the filter has no options in the current context.
const FILTERABLE_COLUMN_IDS = [
  "status",
  "state",
  "assigned_to",
  "screening_status",
  "property",
  "property_group",
  "unit",
  "unit_type",
  "stage",
  "submitted_at",
  "started_at",
  "updated_at",
  "desired_move_in_date"
];

const buildStateColumns = (
  pathname: string,
  sortableKeys: string[]
): ColDef<ApplicationWithUIStatus>[] =>
  pathname.includes("properties")
    ? []
    : [
        {
          headerName: "State",
          field: "property.address.state_code" as never,
          colId: "state",
          width: 100,
          minWidth: 80,
          resizable: true,
          sortable: sortableKeys.includes("state"),
          comparator: preserveRowOrderComparator,
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => (
            <span className="block truncate text-content-500">
              {params.data?.property.address.state_code ||
                toStateCode(params.data?.property.address.state_or_province) ||
                "__"}
            </span>
          )
        }
      ];

const trackedOpenApplicationNotes = (
  openApplicationNotes: ((id: number) => void) | undefined,
  hasNote: boolean
) => {
  if (!openApplicationNotes) return;
  return (id: number) => {
    track("applications_table.last_note.selected", {
      application_id: String(id),
      has_note: hasNote
    });
    openApplicationNotes(id);
  };
};

const EMPTY_APPLICANT_PROGRESS: ApplicantProgressContextValue = {
  progressByApplicationId: new Map()
};

// Chat column cell. Subscribes to the shared unread-by-contact snapshot itself,
// so the column definitions stay stable while badges refresh on each poll.
const ChatCell = ({
  row,
  onChatClick
}: {
  row: ApplicationWithUIStatus;
  onChatClick: (application: Application) => void;
}) => {
  const unreadByContact = useUnreadByContact();
  // Slim list rows only carry crm_contact_id once the API exposes it (same
  // branch); rows still served from the API's warm cache read as 0 unread.
  const unreads = row.applicants.reduce(
    (sum, applicant) =>
      sum +
      (applicant.crm_contact_id
        ? (unreadByContact.get(applicant.crm_contact_id) ?? 0)
        : 0),
    0
  );
  const hasApplicant = row.applicants.length > 0;

  return (
    <div
      className="flex items-center justify-center w-full h-full"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="relative inline-flex">
        <IconButton
          size={IconButtonSize.XS}
          variant={IconButtonVariant.TEXT_BASE}
          HugeIcon={BubbleChatIcon}
          disabled={!hasApplicant}
          tooltip={hasApplicant ? "Message" : "No applicant to message yet"}
          onClick={() => onChatClick(row)}
        />
        <ChatBadge count={unreads} className="pointer-events-none" />
      </span>
    </div>
  );
};

export const ApplicationsDataGridTable = ({
  groupBy,
  applications,
  listQuery,
  activeTab,
  activeStageName,
  hasSearchParams,
  isLoading,
  setSelectedRentersRow,
  selectedRentersRow,
  onCancelClick,
  onUncancelClick,
  onMoveClick,
  onRefundClick,
  onSplitClick,
  sorting,
  setSorting,
  sortableKeys,
  columnsVisibility,
  invalidateQueries,
  pagination,
  pagesCount = 1,
  perPageRows,
  viewPortHeightOffset,
  openApplicationNotes,
  onChatClick,
  applicantProgress,
  onClearFilters,
  onColumnOrderChange
}: {
  groupBy?: string;
  applications: Application[];
  /** The list query (minus paging), passed in router state on row open for the application page's next/previous. */
  listQuery: string;
  onInviteClick?: () => void;
  activeTab?: ActiveTab;
  activeStageName?: string;
  isPending?: boolean;
  isCanceled?: boolean;
  hasSearchParams?: boolean;
  setSelectedRentersRow: Dispatch<SetStateAction<Record<string, boolean>>>;
  selectedRentersRow: Record<string, boolean>;
  onCancelClick?: (id: string) => void;
  onUncancelClick?: (id: string) => void;
  onMoveClick?: (id: string) => void;
  onRefundClick?: (id: string) => void;
  onSplitClick?: (id: string) => void;
  isLoading?: boolean;
  sorting: { id: string; desc: boolean }[];
  setSorting: Dispatch<SetStateAction<{ id: string; desc: boolean }[]>>;
  sortableKeys: string[];
  invalidateQueries?: unknown[][];
  columnsVisibility: { id: string; checked: boolean; disabled: boolean }[];
  pagination?: React.ReactNode;
  pagesCount?: number;
  perPageRows: number;
  viewPortHeightOffset?: number;
  openApplicationNotes?: (id: number) => void;
  onChatClick?: (application: Application) => void;
  applicantProgress?: ApplicantProgressContextValue;
  /** Set while filters are active: the empty state then offers to clear them. */
  onClearFilters?: () => void;
  /** Called after a header drop with the shown columns' new order. */
  onColumnOrderChange: (shownOrder: string[]) => void;
}) => {
  const { Unit, unit, Property } = useUnitsPropertiesDictionary();
  const { pathname } = useLocation();
  const { permissions } = useAuthContext();
  const { sectionHeight } = useLayout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const gridApiRef = useRef<GridApi | null>(null);
  // Who's in the applicant app now, for the rows on this page (BoomView).
  const visibleApplicationIds = useMemo(
    () => applications.map((application) => Number(application.id)),
    [applications]
  );
  const livePresence = useLivePresence(visibleApplicationIds);
  // Read at click time so a filter change does not rebuild the columnDefs.
  const listQueryRef = useRef(listQuery);
  listQueryRef.current = listQuery;

  // "View live" on a row: open the application and ask that applicant to share.
  const viewLive = useCallback(
    (applicationId: number, applicantId: number | null) => {
      navigate(
        {
          pathname: `/applications/${applicationId}`,
          search: applicantId ? `?${VIEW_LIVE_PARAM}=${applicantId}` : ""
        },
        { state: { applicationsListQuery: listQueryRef.current } }
      );
    },
    [navigate]
  );

  const [selectedRows, setSelectedRows] = useState<Set<string>>(
    new Set(
      Object.keys(selectedRentersRow).filter((k) => selectedRentersRow[k])
    )
  );

  const tableApplications = useMemo<ApplicationWithUIStatus[]>(() => {
    const appsWithStatus = applications.map((application) => ({
      ...application,
      ui_status: getApplicationUIStatus(
        application as unknown as ApplicationLike
      )
    }));

    if (!groupBy || groupBy === "") return appsWithStatus;

    const groupByMap: Record<string, string> = {
      unit: "unit.title",
      status: "ui_status",
      screening_status: "screening_status",
      stage: "stage.id"
    };

    const groupByKey = groupByMap[groupBy] || groupBy;
    const grouped = new Map<string, ApplicationWithUIStatus[]>();

    appsWithStatus.forEach((app) => {
      const groupValue = String(getNestedValue(app, groupByKey) || "");
      if (!grouped.has(groupValue)) grouped.set(groupValue, []);
      grouped.get(groupValue)!.push(app);
    });

    const result: ApplicationWithUIStatus[] = [];
    grouped.forEach((apps, groupValue) => {
      result.push({
        ...apps[0],
        id: `group-${groupValue}` as unknown as number,
        isGroupHeader: true,
        groupValue
      });
      result.push(...apps);
    });

    return result;
  }, [applications, groupBy]);

  const currentTab = {
    isAll: activeTab === ActiveTab.All,
    isNotSubmitted: activeTab === ActiveTab.NotSubmitted,
    isPartiallySubmitted: activeTab === ActiveTab.PartiallySubmitted,
    isSubmitted: activeTab === ActiveTab.Submitted,
    isApproved: activeTab === ActiveTab.Approved,
    isConditionallyApproved: activeTab === ActiveTab.ApprovedConditionally,
    isDeclined: activeTab === ActiveTab.Declined,
    isCanceled: activeTab === ActiveTab.Canceled,
    isOnHold: activeTab === ActiveTab.OnHold
  };

  // Columns render in the user's saved order for this tab (CRM-1891).
  const sortedColumnsVisibility = columnsVisibility;
  const columnOrder = useMemo(
    () => columnsVisibility.map((col) => col.id),
    [columnsVisibility]
  );
  const reorder = useColumnReorder({
    order: columnOrder,
    onReorder: onColumnOrderChange
  });

  const onResumeHoldSideEffect = () => {
    invalidateQueries?.forEach((queryKey) => {
      queryClient.refetchQueries({
        queryKey
      });
    });
  };

  const toggleRowSelection = (id: string) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);

      const newState: Record<string, boolean> = {};
      newSet.forEach((rowId) => {
        newState[rowId] = true;
      });
      setSelectedRentersRow(newState);

      return newSet;
    });
  };

  const columns = useMemo<ColDef<ApplicationWithUIStatus>[]>(() => {
    const cols: ColDef<ApplicationWithUIStatus>[] = [];

    // Checkbox column
    cols.push({
      headerName: "",
      colId: "checkbox",
      field: "id" as never,
      pinned: "left",
      width: 64,
      resizable: false,
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<ApplicationWithUIStatus>) => {
        const row = params.data;
        if (!row) return null;

        return (
          <div
            className="flex items-center justify-center w-full h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip placement="top-end">
              <TooltipTrigger>
                <Checkbox
                  wrapperClassName="!w-auto !flex-none !p-3 justify-center hover:bg-alpha-40 rounded-full"
                  size={CheckboxSize.SM}
                  checked={selectedRows.has(String(row.id))}
                  disabled={row.status === ApplicationStatus.OnHold}
                  onChange={() => toggleRowSelection(String(row.id))}
                />
                <Show if={row.status === ApplicationStatus.OnHold}>
                  <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white">
                    Resume this application before selecting it for merging
                  </TooltipContent>
                </Show>
              </TooltipTrigger>
            </Tooltip>
          </div>
        );
      }
    });

    // Dynamic columns based on visibility
    sortedColumnsVisibility.forEach((col) => {
      if (!col.checked || col.disabled) return;

      if (col.id === "criteria")
        cols.push({
          headerName: "Criteria",
          colId: "criteria",
          field: "applicant_score" as never,
          resizable: false,
          width: 90,
          sortable: false,
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            const templateCriteriaSettings =
              row.verification_template?.screening_criteria;

            const hasValue =
              [
                ApplicationStatus.Submitted,
                ApplicationStatus.Approved,
                ApplicationStatus.Rejected,
                ApplicationStatus.ConditionalApproved,
                ApplicationStatus.Canceled
              ].includes(row.status) ||
              row.first_submitted_at ||
              row.submitted_at;

            if (
              !row.verification_template?.recommendation_method ||
              row.verification_template.recommendation_method === "none"
            )
              return null;

            if (
              row.verification_template.recommendation_method === "score_based"
            )
              return (
                <div className="overflow-hidden pl-2">
                  <Suspense fallback={<Loader />}>
                    <ScoreGraph
                      size={ScoreGraphSize.XS}
                      score={hasValue ? row.score : null}
                      declineRangeEnd={
                        templateCriteriaSettings?.decline_range_end
                      }
                      conditionalApprovalRangeEnd={
                        templateCriteriaSettings?.conditional_approval_range_end
                      }
                    />
                  </Suspense>
                </div>
              );

            return (
              <div className="overflow-hidden pl-6">
                <RuleBaseGraph
                  recommendation={hasValue ? row.recommendation : null}
                />
              </div>
            );
          }
        });

      if (col.id === "applicants")
        cols.push({
          headerName: "Applicants",
          field: "applicants" as never,
          width: 240,
          pinned: "left",
          resizable: true,
          sortable: sortableKeys.includes("applicants"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            const { applicants, pending_magic_links } = row;

            const total_applicants =
              (applicants.length || 0) + (pending_magic_links?.length || 0);

            const applicantNames = getApplicantNames(
              applicants as unknown as Parameters<typeof getApplicantNames>[0]
            );
            const inviteesEmails = getInviteesEmails(pending_magic_links || []);

            const numApplicantsPrefix =
              total_applicants > 1 ? `(${total_applicants}) ` : "";
            const names = (
              <p className="truncate mr-2">
                {`${numApplicantsPrefix} ${getCombinedNamesAndEmails(applicantNames, inviteesEmails)}`}
              </p>
            );
            const namesWithProgress =
              total_applicants === 0 ? (
                names
              ) : (
                <ApplicantProgressHover application={row}>
                  {names}
                </ApplicantProgressHover>
              );

            if (!row.bypass_requested)
              return total_applicants === 0 ? (
                names
              ) : (
                <div className="flex h-full min-w-0 items-center gap-1.5">
                  <LivePresenceDot applicationId={row.id} />
                  {namesWithProgress}
                  <LiveViewRowAction
                    applicationId={row.id}
                    onViewLive={viewLive}
                  />
                </div>
              );

            const { applicant, requested_at } = row.bypass_requested;

            return (
              <div className="flex h-full min-w-0 items-center gap-1.5">
                <Tooltip placement="top-start">
                  <TooltipTrigger asChild>
                    <span className="flex shrink-0">
                      <HugeiconsIcon
                        icon={Flag02Icon}
                        size={14}
                        className="text-danger-500"
                        aria-label="Asked to bypass identity verification"
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="z-50 rounded-lg border bg-alpha-800 px-4 py-3 text-sm text-white">
                    {getFullName({
                      firstName: applicant.first_name,
                      lastName: applicant.last_name,
                      middleName: applicant.middle_name,
                      short: true
                    })}{" "}
                    asked to bypass identity verification ·{" "}
                    {formatCalendar(requested_at)}
                  </TooltipContent>
                </Tooltip>
                <LivePresenceDot applicationId={row.id} />
                {namesWithProgress}
                <LiveViewRowAction
                  applicationId={row.id}
                  onViewLive={viewLive}
                />
              </div>
            );
          }
        });

      if (col.id === "property" && !pathname.includes("properties"))
        cols.push({
          headerName: Property,
          field: "property.name" as never,
          colId: "property",
          width: 240,
          resizable: true,
          sortable: sortableKeys.includes("property"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            return (
              <span className="block 2xl:max-w-none space-y-2">
                <span className="text-sm font-medium text-content-1000 truncate block">
                  {row.property.name}
                </span>
                <span className="text-xs text-content-400 truncate block">
                  {row.property.address.address1}
                </span>
              </span>
            );
          }
        });

      if (col.id === "state")
        cols.push(...buildStateColumns(pathname, sortableKeys));

      if (col.id === "property_group")
        cols.push({
          headerName: `${Property} group`,
          field: "property.property_group.name" as never,
          colId: "property_group",
          width: 160,
          minWidth: 120,
          resizable: true,
          sortable: sortableKeys.includes("property_group"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            return (
              <span className="block overflow-hidden text-ellipsis whitespace-nowrap 2xl:max-w-[140px] max-w-[80px]">
                {row.property.property_group?.name ?? "--"}
              </span>
            );
          }
        });

      if (col.id === "unit")
        cols.push({
          headerName: Unit,
          field: "unit.title" as never,
          colId: "unit",
          width: 98,
          minWidth: 98,
          resizable: true,
          sortable: sortableKeys.includes("unit"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            const isUnlisted = row.unit_delisted === true;

            return (
              <div className="flex items-center gap-1">
                {isUnlisted && (
                  <Tooltip placement="top">
                    <TooltipTrigger>
                      <div className="bg-danger-50 flex items-center justify-center p-1 rounded-2xl shrink-0">
                        <HugeiconsIcon
                          icon={UnavailableIcon}
                          size={12}
                          className="text-danger-500"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white max-w-[250px]">
                      Applications for {row.unit.title} are no longer allowed
                    </TooltipContent>
                  </Tooltip>
                )}
                <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-content-500">
                  {row.unit.title}
                </span>
              </div>
            );
          }
        });

      if (col.id === "unit_type")
        cols.push({
          headerName: "Unit type",
          field: "unit.unit_type" as never,
          colId: "unit_type",
          width: 140,
          minWidth: 120,
          resizable: true,
          sortable: sortableKeys.includes("unit_type"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            return (
              <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-content-500">
                {row.unit?.unit_type || "—"}
              </span>
            );
          }
        });

      if (col.id === "submitted_at")
        cols.push({
          headerName: "Submitted",
          colId: "submitted_at",
          field: "submitted_at",
          width: 110,
          minWidth: 110,
          sortable: sortableKeys.includes("submitted_at"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            return (
              <DateTimeTableCell
                className="whitespace-nowrap"
                value={row.submitted_at}
              />
            );
          }
        });

      if (col.id === "desired_move_in_date")
        cols.push({
          headerName: "Move-in date",
          colId: "desired_move_in_date",
          field: "desired_move_in_date",
          width: 140,
          minWidth: 140,
          sortable: sortableKeys.includes("desired_move_in_date"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data;
            if (!row) return null;

            return (
              <DateTableCell
                className="whitespace-nowrap"
                value={row.desired_move_in_date}
              />
            );
          }
        });

      if (col.id === "started_at")
        cols.push({
          headerName: "Started",
          colId: "started_at",
          field: "started_at",
          width: 110,
          minWidth: 110,
          sortable: sortableKeys.includes("started_at"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            return (
              <DateTimeTableCell
                className="whitespace-nowrap"
                value={row.started_at}
              />
            );
          }
        });

      if (col.id === "last_note")
        cols.push({
          headerName: "Last note",
          colId: "last_note",
          field: "last_note",
          width: 300,
          minWidth: 150,
          resizable: true,
          sortable: sortableKeys.includes("last_note"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => (
            <LastNoteCell
              note={params.data?.last_note}
              applicationId={params.data?.id}
              openApplicationNotes={trackedOpenApplicationNotes(
                openApplicationNotes,
                Boolean(params.data?.last_note)
              )}
            />
          )
        });

      if (col.id === "updated_at")
        cols.push({
          headerName: "Updated at",
          colId: "updated_at",
          field: "updated_at",
          width: 135,
          minWidth: 135,
          sortable: sortableKeys.includes("updated_at"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            return (
              <DateTimeTableCell
                className="whitespace-nowrap"
                value={row.updated_at}
              />
            );
          }
        });

      if (col.id === "assigned_to")
        cols.push({
          headerName: "Assigned to",
          field: "assignee_ids" as never,
          colId: "assigned_to",
          width: 240,
          minWidth: 200,
          resizable: true,
          sortable: sortableKeys.includes("assigned_to"),
          cellRenderer: (
            params: ICellRendererParams<ApplicationWithUIStatus>
          ) => {
            const row = params.data as ApplicationWithUIStatus | undefined;
            if (!row) return null;

            if (row.isGroupHeader) return null;

            return (
              <AssigneeSelect
                value={row.assignee_ids?.[0] ?? null}
                rowId={String(row.id)}
                propertyId={row.property?.id}
              />
            );
          }
        });
    });

    // Status column
    if (
      sortedColumnsVisibility.find((col) => col.id === "status" && col.checked)
    )
      cols.push({
        headerName: "Application status",
        colId: "status",
        field: "status",
        width: 170,
        minWidth: 130,
        resizable: true,
        sortable: sortableKeys.includes("status"),
        cellRenderer: (
          params: ICellRendererParams<ApplicationWithUIStatus>
        ) => {
          const row = params.data as ApplicationWithUIStatus | undefined;
          if (!row) return null;

          const { conditions, status, auto_approved, ui_status, applicants } =
            row;
          const applicationStatus = ui_status;

          const isAutoCanceled =
            status === ApplicationStatus.Canceled &&
            applicants.every(
              (applicant) =>
                applicant.application_status === ApplicantStatus.Canceled &&
                applicant.canceled_by_auto_cancel
            ) &&
            !applicants.some(
              (applicant) =>
                applicant.application_status === ApplicantStatus.Submitted
            );

          return (
            <div className="flex w-full">
              <ApplicationStatusBadge
                status={applicationStatus}
                conditions={conditions}
                type="application"
                isAutoCanceled={isAutoCanceled}
                autoApproved={auto_approved}
              />
            </div>
          );
        }
      });

    if (
      sortedColumnsVisibility.find((col) => col.id === "stage" && col.checked)
    )
      cols.push({
        headerName: "Stage",
        colId: "stage",
        field: "stage" as never,
        width: 190,
        minWidth: 140,
        resizable: true,
        sortable: sortableKeys.includes("stage"),
        cellRenderer: (
          params: ICellRendererParams<ApplicationWithUIStatus>
        ) => {
          const row = params.data;
          if (!row || row.isGroupHeader) return null;

          return (
            <div
              className="flex items-center h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <ApplicationStageSelect application={row} />
            </div>
          );
        }
      });

    // Chat column — hidden by default via column visibility (CRM-1133). Pinned
    // right so it sits left of the actions menu; the pop-up resolves the
    // conversation from the row's applicant.
    if (
      onChatClick &&
      sortedColumnsVisibility.find((col) => col.id === "chat" && col.checked)
    )
      cols.push({
        headerName: "",
        colId: "chat",
        width: 60,
        minWidth: 60,
        pinned: "right",
        resizable: false,
        sortable: false,
        suppressMovable: true,
        // Divider between the chat and actions columns (design, 2026-09-02);
        // the pinned-right container already draws the one before chat.
        cellClass: "border-r border-r-alpha-40",
        headerClass: "border-r border-alpha-40",
        cellRenderer: (
          params: ICellRendererParams<ApplicationWithUIStatus>
        ) => {
          const row = params.data;
          if (!row || row.isGroupHeader) return null;
          return <ChatCell row={row} onChatClick={onChatClick} />;
        }
      });

    // Actions column
    const actionPerms = permissions;

    cols.push({
      headerName: "",
      field: "id" as never,
      colId: "actions",
      width: 60,
      minWidth: 60,
      pinned: "right",
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<ApplicationWithUIStatus>) => {
        const row = params.data;
        if (!row) return null;

        const id = String(row.id);
        const status = row.status as ApplicationStatus;
        const appActions = new ApplicationActions({
          status,
          permissions: actionPerms,
          applicants: row.applicants,
          application: row,
          unitLabel: unit,
          selectedForMerge: selectedRows.has(id)
        });

        const menuActions: {
          title: string | JSX.Element;
          onClick: () => void;
          disabled?: boolean;
          tooltip?: string;
        }[] = [
          {
            title: "Review",
            onClick: () => {
              navigate(`/applications/${id}`, {
                state: { applicationsListQuery: listQueryRef.current }
              });
            }
          }
        ];

        // Resume / Place on hold
        const { resume, placeOnHold, move, split, uncancel, cancel, refund } =
          appActions;

        if (!resume.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <HugeiconsIcon
                  icon={PlayIcon}
                  size={20}
                  className="text-content-200 shrink-0"
                />
                Resume
              </span>
            ),
            onClick: () => {
              resumeApplication({
                applicationId: id,
                onSuccess: onResumeHoldSideEffect
              });
            }
          });
        else if (!placeOnHold.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <HugeiconsIcon
                  icon={PauseIcon}
                  size={20}
                  className="text-content-200 shrink-0"
                />
                Place on hold
              </span>
            ),
            disabled: placeOnHold.disabled,
            tooltip: placeOnHold.tooltip,
            onClick: () => {
              placeOnHoldApplication({
                applicationId: id,
                onSuccess: onResumeHoldSideEffect
              });
            }
          });

        // Move to another unit
        if (!move.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <ArrowRight size="18" className="stroke-content-200" />
                Move to another {unit}
              </span>
            ),
            tooltip: move.tooltip,
            disabled: move.disabled,
            onClick: () => onMoveClick?.(id)
          });

        // Split application
        if (!split.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <HugeiconsIcon
                  icon={PathfinderDivideIcon}
                  size={18}
                  className="text-content-200 shrink-0"
                />
                Split application
              </span>
            ),
            disabled: split.disabled,
            tooltip: split.tooltip,
            onClick: () => onSplitClick?.(id)
          });

        // Uncancel / Cancel
        if (!uncancel.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <Archive size="18" className="stroke-content-200" />
                Uncancel application
              </span>
            ),
            disabled: uncancel.disabled,
            tooltip: uncancel.tooltip,
            onClick: () => onUncancelClick?.(id)
          });
        else if (!cancel.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <Archive size="18" className="stroke-content-200" />
                Cancel application
              </span>
            ),
            tooltip: cancel.tooltip,
            disabled: cancel.disabled,
            onClick: () => onCancelClick?.(id)
          });

        // Refund application
        if (!refund.hidden)
          menuActions.push({
            title: (
              <span className="flex gap-2 items-center">
                <HugeiconsIcon
                  icon={CashbackIcon as IconSvgElement}
                  className="text-content-200"
                  size={18}
                />
                Refund application
              </span>
            ),
            disabled: refund.disabled,
            tooltip: refund.tooltip,
            onClick: () => onRefundClick?.(id)
          });

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <ContextMenuPopover
              placement="bottom-start"
              actions={menuActions}
            />
          </div>
        );
      }
    });

    // Screening status column (last, flex to fill remaining space)
    cols.push({
      headerName: "Screening status",
      field: "screening_status",
      width: 150,
      minWidth: 120,
      flex: 1,
      resizable: true,
      sortable: sortableKeys.includes("screening_status"),
      cellRenderer: (params: ICellRendererParams<ApplicationWithUIStatus>) => {
        const row = params.data;
        if (!row) return null;

        return (
          <ScreeningStatusBadge
            status={row.screening_status}
            reverificationRequests={row.open_reverification_requests}
          />
        );
      }
    });

    // Rows arrive sorted by the server, so AG Grid must keep their order instead of re-sorting
    // the page client-side (which would disagree for keys like stage or assigned_to).
    return arrangeColumnDefs(cols, columnOrder).map((col) => {
      const colId = col.colId ?? (col.field as string | undefined);
      return {
        ...col,
        ...(col.sortable ? { comparator: preserveRowOrderComparator } : {}),
        ...(colId && FILTERABLE_COLUMN_IDS.includes(colId)
          ? {
              headerComponentParams: {
                innerHeaderComponent: ColumnFilterHeader
              }
            }
          : {})
      };
    });
  }, [
    sortedColumnsVisibility,
    columnOrder,
    pathname,
    Property,
    Unit,
    selectedRows,
    sortableKeys,
    groupBy,
    onChatClick,
    viewLive
  ]);

  const onGridReady = useCallback(
    (params: GridReadyEvent<ApplicationWithUIStatus>) => {
      gridApiRef.current = params.api;
    },
    []
  );

  const onSortChanged = useCallback(
    (event: SortChangedEvent) => {
      const colState = event.api.getColumnState();
      const sortedCol = colState.find((c) => c.sort);
      if (sortedCol)
        setSorting([
          {
            id: sortedCol.colId,
            desc: sortedCol.sort === "desc"
          }
        ]);
      else setSorting([]);
    },
    [setSorting]
  );

  // Apply external sort state to ag-grid
  useEffect(() => {
    if (!gridApiRef.current) return;
    const columnState = sorting.map((s) => ({
      colId: s.id,
      sort: s.desc ? ("desc" as const) : ("asc" as const)
    }));
    gridApiRef.current.applyColumnState({
      state: columnState,
      defaultState: { sort: null }
    });
  }, [sorting]);

  const onCellClicked = useCallback(
    (event: CellClickedEvent<ApplicationWithUIStatus>) => {
      const row = event.data;
      if (!row || row.isGroupHeader) return;
      const colId = event.column.getColId();
      // The grid hears clicks before React does: "View live" navigates itself.
      const target = event.event?.target;
      if (
        target instanceof Element &&
        target.closest(`[${LIVE_VIEW_ROW_ACTION_ATTR}]`)
      )
        return;
      if (
        colId === "checkbox" ||
        colId === "actions" ||
        colId === "chat" ||
        colId === "stage" ||
        colId === "assigned_to" ||
        colId === "last_note"
      )
        return;
      navigate(`/applications/${row.id}`, {
        state: { applicationsListQuery: listQueryRef.current }
      });
    },
    [navigate]
  );

  const [emptyStateTitle, EmptyStateIcon] = useMemo<
    [string, IconSvgElement]
  >(() => {
    if (hasSearchParams) return ["No results found", Search01Icon];

    if (activeStageName)
      return [
        `Applications in ${activeStageName} will appear here`,
        File02Icon
      ];

    if (currentTab.isAll) return ["Applications will appear here", File02Icon];

    let prefix: string = activeTab || "";
    if (currentTab.isConditionallyApproved) prefix = "Conditionally approved";
    else if (currentTab.isOnHold) prefix = "On hold";
    else prefix = capitalize(camelToSentence(prefix).toLowerCase());

    const title = currentTab.isSubmitted
      ? "Submitted applications will appear here"
      : `${prefix || ""} applications will appear here`;

    const Icon = File02Icon;

    return [title, Icon];
  }, [currentTab, activeTab, activeStageName, hasSearchParams]);

  const getRowId = useCallback(
    (params: GetRowIdParams<ApplicationWithUIStatus>) => String(params.data.id),
    []
  );

  const isFullWidthRow = useCallback(
    (params: IsFullWidthRowParams<ApplicationWithUIStatus>) =>
      !!params.rowNode.data?.isGroupHeader,
    []
  );

  const fullWidthCellRenderer = useCallback(
    (params: ICellRendererParams<ApplicationWithUIStatus>) => {
      const row = params.data;
      if (!row) return null;

      if (groupBy === "stage")
        return (
          <div className="flex items-center h-full px-4">
            {row.stage ? (
              <StagePill stage={row.stage} />
            ) : (
              <span className="text-content-400 text-sm font-medium">
                No stage
              </span>
            )}
          </div>
        );

      if (!row.groupValue) return null;

      if (groupBy === "status" || groupBy === "ui_status")
        return (
          <div className="flex items-center h-full px-4">
            <ApplicationStatusBadge
              type="application"
              status={row.groupValue as ApplicationStatus}
            />
          </div>
        );

      if (groupBy === "screening_status")
        return (
          <div className="flex items-center h-full px-4">
            <ScreeningStatusBadge status={row.groupValue} />
          </div>
        );

      return (
        <div className="flex items-center h-full px-4">
          <span className="text-content-400 text-sm font-medium">
            {row.groupValue}
          </span>
        </div>
      );
    },
    [groupBy]
  );

  return (
    <ApplicantProgressContext.Provider
      value={applicantProgress ?? EMPTY_APPLICANT_PROGRESS}
    >
      <LivePresenceContext.Provider value={livePresence.byApplication}>
        <div {...reorder.containerProps}>
          {reorder.highlight}
          <AgGrid<ApplicationWithUIStatus>
            {...reorder.gridProps}
            columns={columns}
            rows={tableApplications}
            loading={isLoading}
            perPageRows={perPageRows}
            viewPortHeight={sectionHeight}
            viewPortHeightOffset={
              viewPortHeightOffset ?? DEFAULT_AG_GRID_VIEWPORT_OFFSET
            }
            getRowClass={(params) =>
              params.data?.bypass_requested
                ? "!bg-danger-100/20 [--ag-row-hover-color:transparent] [&.ag-row-hover]:!bg-danger-100/40"
                : undefined
            }
            getRowHeight={(params) => {
              if (params.data?.isGroupHeader) return 48;
              return 64;
            }}
            isFullWidthRow={isFullWidthRow}
            fullWidthCellRenderer={fullWidthCellRenderer}
            onGridReady={onGridReady}
            onSortChanged={onSortChanged}
            onCellClicked={onCellClicked}
            getRowId={getRowId}
            footer={pagination}
            showFooter={pagesCount > 1}
            noRowsFallback={
              onClearFilters ? (
                <NoRowsFallback
                  Icon={Search01Icon}
                  title={
                    hasSearchParams
                      ? "No applications match your search and filters"
                      : "No applications match these filters"
                  }
                  text={
                    hasSearchParams
                      ? "Try another search, or remove a filter or clear them all."
                      : "Try removing a filter or clearing them all."
                  }
                  buttonText="Clear filters"
                  buttonAction={onClearFilters}
                />
              ) : (
                <NoRowsFallback
                  Icon={EmptyStateIcon}
                  iconStrokeWidth={!hasSearchParams ? 1.5 : undefined}
                  title={emptyStateTitle}
                  text={
                    hasSearchParams
                      ? "We couldn't find any data matching your query"
                      : null
                  }
                />
              )
            }
          />
        </div>
      </LivePresenceContext.Provider>
    </ApplicantProgressContext.Provider>
  );
};
